import { Flex } from 'components/Flex';

import { DeleteButton } from 'modals/ShiftModal/src/views/DetailView/DetailTabView/DetailTabView.styled';
import { BKJButton } from '@bookjane2/bookjane-design-library';
import { Fragment } from 'react';
import { useTranslation } from 'hooks/useTranslation';
import { useSelector } from 'react-redux';
import { UserGuard } from 'guards/UserGuard';
import { UserPermissionEnum } from 'guards/UserGuard.types';
import { IDetailViewFooterProps } from 'modals/ShiftModal/src/views/DetailView/DetailView.types';
import { isEditCompletedShiftsFeatureEnabledSelector } from 'store/selectors/featureFlagSelectors';
import { isUserReadOnlySelector } from 'store/selectors/userSelectors/isUserReadOnlySelector';
import { getCurrentlySelectedLocation } from 'utils/getCurrentlySelectedLocation';
import { OrderStatusEnum } from 'types/graphql';
import { UnableToEditMessage } from 'modals/ShiftModal/src/views/DetailView/UnableToEditMessage';
import { DeleteBlockDialog } from 'components/DeleteBlockDialog';
import { useShiftModalPropsContext } from 'modals/ShiftModal/ShiftModal.props.context';
import { shiftModalRoutingService } from 'modals/ShiftModal/ShiftModal.ui.context';

export function WebDetailViewFooter(props: IDetailViewFooterProps) {
  const {
    order,
    validateAndExecute,
    goToSendToAgenciesView,
    goToReleaseShiftView,
    goToEditView,
    goToDeleteView,
    isBlockShift,
    isBlockDeleteConfirmationOpen,
    setIsBlockDeleteConfirmationOpen,
  } = props;

  const { t } = useTranslation();
  const { refreshBackgroundPage } = useShiftModalPropsContext();

  const { value: locationId } = getCurrentlySelectedLocation();

  const isEditCompletedShiftsFeatureEnabled = useSelector(
    isEditCompletedShiftsFeatureEnabledSelector(Number.parseInt(locationId)),
  );

  const isCompletedShiftNotEditable =
    order.status?.value === OrderStatusEnum.completed &&
    !order.isEditable &&
    isEditCompletedShiftsFeatureEnabled;

  const isReadOnly = useSelector(isUserReadOnlySelector);

  if (isReadOnly) {
    return <Fragment />;
  }

  return (
    <Fragment>
      <UserGuard
        userType={['USER_TYPE_B2B', 'USER_TYPE_PREMIUM_SECONDARY', 'USER_TYPE_ESSENTIAL_SECONDARY']}
      >
        {!!order.isDeletable && (
          <Flex>
            <DeleteButton
              variant="RedText"
              onClick={isBlockShift ? () => setIsBlockDeleteConfirmationOpen(true) : goToDeleteView}
            >
              {t(isBlockShift ? 'shift_modal:DELETE_BLOCK' : 'shift_modal:DELETE')}
            </DeleteButton>
          </Flex>
        )}
        {isBlockShift && !!order.shiftBlocks?.nodes?.[0]?.id && (
          <DeleteBlockDialog
            isOpen={isBlockDeleteConfirmationOpen}
            setIsOpen={setIsBlockDeleteConfirmationOpen}
            blockId={order.shiftBlocks.nodes[0].id}
            onSuccess={() => {
              refreshBackgroundPage();
              shiftModalRoutingService.close();
            }}
          />
        )}
      </UserGuard>

      <Flex width="100%" justifyContent="flex-end" gap="8px">
        <UnableToEditMessage />
        {(!!order.isEditable || isCompletedShiftNotEditable) && (
          <BKJButton
            width="180px"
            variant="PurpleOutlined"
            onClick={goToEditView}
            disabled={!order.isEditable}
          >
            {t('shift_modal:EDIT')}
          </BKJButton>
        )}
        {order.isJaneReleaseAllowed && order.status?.value !== OrderStatusEnum.in_progress && (
          <BKJButton width="180px" variant="PurpleSolid" onClick={goToReleaseShiftView}>
            {t('shift_modal:RELEASE')}
          </BKJButton>
        )}
        <UserGuard
          userType={[
            UserPermissionEnum.USER_TYPE_B2B,
            UserPermissionEnum.USER_TYPE_PREMIUM_PRIMARY,
            UserPermissionEnum.USER_TYPE_PREMIUM_SECONDARY,
          ]}
        >
          {order.canSendToAgency && (
            <BKJButton width="185px" variant="PurpleSolid" onClick={goToSendToAgenciesView}>
              {t('shift_modal:SEND_TO_AGENCY')}
            </BKJButton>
          )}
        </UserGuard>
        <UserGuard
          userType={[
            UserPermissionEnum.USER_TYPE_B2B,
            UserPermissionEnum.USER_TYPE_PREMIUM_PRIMARY,
            UserPermissionEnum.USER_TYPE_PREMIUM_SECONDARY,
          ]}
        >
          {order.canB2bAssign && (
            <BKJButton width="180px" variant="PurpleSolid" onClick={validateAndExecute}>
              {t('shift_modal:ASSIGN')}
            </BKJButton>
          )}
        </UserGuard>
        <UserGuard
          userType={[
            UserPermissionEnum.USER_TYPE_AGENCY,
            UserPermissionEnum.USER_TYPE_AGENCY_PRIMARY,
            UserPermissionEnum.USER_TYPE_AGENCY_SECONDARY,
          ]}
        >
          {order.canAssign && (
            <BKJButton width="180px" variant="PurpleSolid" onClick={validateAndExecute}>
              {t('shift_modal:ASSIGN')}
            </BKJButton>
          )}
        </UserGuard>
      </Flex>
    </Fragment>
  );
}
